<template>
  <div class="px-3">
    <h1 class="font--m-3 mb-2">업체 현황</h1>

    <!-- 업체명 -->
    <FormValidationInput
      :inputValue.sync="form.companyName"
      :valid="{ name: '업체명', rules: 'required' }"
      title="업체명(사업자등록증명원 상의 상호기입)"
      placeholder="업체명을 입력해주세요"
    />

    <!-- 사업자등록번호 -->
    <FormValidationInput
      :inputValue.sync="form.businessRegistrationNumber"
      :valid="{ name: '사업자등록번호', rules: 'required|length:12' }"
      title="사업자등록번호"
      placeholder="사업자등록번호를 입력해주세요"
      type="tel"
      oninput="this.value = this.value.replace(/[^0-9]/g, '').substring(0, 10).replace(/^(\d{3})(\d{2})(\d{5})$/, '$1-$2-$3')"
    />

    <!-- 사업개시일 -->
    <ValidationObserver v-slot="{ errors }" class="input-box" tag="div">
      <p>사업개시일</p>
      <v-layout>
        <ValidationProvider name="사업개시 연도" rules="required" slim>
          <v-select
            style="flex-basis: 150px"
            v-model="form.businessStartYear"
            :items="years"
            outlined
            suffix="년"
            hide-details
            :menu-props="{ left: true, minWidth: '180px' }"
            placeholder="-"
            class="year-select"
          />
        </ValidationProvider>

        <ValidationProvider name="사업개시 월" rules="required" slim>
          <v-select
            style="flex-basis: 130px"
            v-model="form.businessStartMonth"
            :items="months"
            outlined
            suffix="월"
            hide-details
            placeholder="-"
          />
        </ValidationProvider>

        <ValidationProvider name="사업개시 일" rules="required" slim>
          <v-select
            style="flex-basis: 130px"
            v-model="form.businessStartDay"
            :items="days"
            outlined
            suffix="일"
            hide-details
            placeholder="-"
          />
        </ValidationProvider>
      </v-layout>
      <v-slide-y-transition>
        <p
          v-if="getFirstErrorMessage(errors)"
          class="position--absolute"
          :class="{ 'opacity-0': !getFirstErrorMessage(errors) }"
        >
          {{ getFirstErrorMessage(errors) }}
        </p>
      </v-slide-y-transition>
    </ValidationObserver>

    <!-- 업태 -->
    <FormValidationInput
      :inputValue.sync="form.businessType"
      :valid="{ name: '사업자 등록증상의 업태', rules: 'required' }"
      title="업태"
      placeholder="사업자 등록증상의 업태"
    />

    <!-- 업종 -->
    <FormValidationInput
      :inputValue.sync="form.businessCategory"
      :valid="{ name: '사업자 등록증상의 업종', rules: 'required' }"
      title="업종"
      placeholder="사업자 등록증상의 업종"
    />

    <!-- 사업장 주소 -->
    <ValidationProvider name="사업장 주소" rules="required" v-slot="{ errors }" slim>
      <div class="input-box">
        <p>사업장 주소</p>
        <v-layout align-center>
          <v-textarea
            v-if="!captureMode"
            v-model="form.workplace.adress"
            rows="2"
            hide-details
            outlined
            no-resize
            :error="!!errors[0]"
            auto-grow
          ></v-textarea>
          <div
            v-else
            class="capture-mode textarea"
            v-html="form.workplace.adress.replace(/\n/g, '<br>')"
          ></div>

          <v-radio-group
            v-model="form.workplace.isOwn"
            hide-details
            dense
            class="mt-0 pt-0"
            style="min-width: 95px"
          >
            <v-radio
              v-for="n in 2"
              :key="n"
              :label="n === 1 ? '자가' : '임차'"
              :value="n === 1 ? true : false"
              class="mb-0"
            ></v-radio>
          </v-radio-group>
        </v-layout>
        <v-slide-y-transition>
          <p
            v-if="errors.length > 0"
            class="position--absolute"
            :class="{ 'opacity-0': errors.length < 1 }"
          >
            {{ errors[0] }}
          </p>
        </v-slide-y-transition>
      </div>
    </ValidationProvider>

    <v-layout>
      <!-- 상시근로자수 -->
      <FormValidationInput
        :inputValue.sync="form.permanentEmployeeCount"
        :valid="{ name: '상시근로자수', rules: 'required|min_value:1' }"
        title="상시근로자수"
        placeholder="상시근로자수"
        suffix="명"
        type="tel"
        class="width-100"
        oninput="this.value = String(Number(this.value.replace(/[^0-9]/g, ''))).substring(0, 4)"
      />

      <!-- 연간매출액('22) 만원단위 -->
      <FormValidationInput
        :inputValue.sync="form.annualRevenue"
        :valid="{ name: '연간매출액', rules: 'required|min_value:1' }"
        title="연간매출액('23)"
        placeholder="23년도 기준"
        suffix="만원"
        type="tel"
        class="width-100"
        oninput="this.value = String(Number(this.value.replace(/[^0-9]/g, ''))).substring(0, 8)"
      />
    </v-layout>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { generateRange } from '@/util/mathUtil.js'
import { getFirstErrorMessage } from '@/plugins/veevalidate'

export default {
  components: {
    FormValidationInput: () => import('@/components/common/form/FormValidationInput.vue'),
  },
  data() {
    return {
      form: {
        // 업체명
        companyName: '',
        // 사업자등록번호
        businessRegistrationNumber: '',

        // 사업개시일
        businessStartYear: '',
        businessStartMonth: '',
        businessStartDay: '',

        // 업태
        businessType: '',
        // 업종
        businessCategory: '',
        // 사업장주소
        workplace: {
          adress: '',
          isOwn: true,
        },
        // 상시근로자수
        permanentEmployeeCount: '',
        // 연간매출액('22) 만원단위
        annualRevenue: '',
      },

      years: generateRange(1950, new Date().getFullYear(), true), // 1950년부터 현재 년도까지의 범위
      months: generateRange(1, 12), // 1월부터 12월까지의 범위
      days: generateRange(1, 31), // 1일부터 31일까지의 범위
    }
  },
  methods: {
    getFirstErrorMessage,
  },
  computed: {
    ...mapState('auth', ['me']),
    ...mapState('ui', ['captureMode']),
  },
  async created() {
    // 업체명 세팅
    this.form.companyName = this.me?.store?.name || ''
    // 사업자등록번호 세팅
    this.form.businessRegistrationNumber =
      this.me?.store?.businessLicenseNumber?.replace(/^(\d{3})(\d{2})(\d{5})$/, '$1-$2-$3') || ''
    // 사업장 주소 세팅
    this.form.workplace.adress = this.me?.store?.address1 || ''
  },
}
</script>

<style lang="scss" scoped>
.v-menu__content.theme--light.menuable__content__active {
  color: red;
  background-color: white;
}

::v-deep.v-select-list .v-list-item {
  padding: 0 8px;
}

.layout {
  column-gap: 4px;
}

::v-deep .year-select .v-input__control > .v-input__slot {
  padding-left: 16px !important;
  padding-right: 4px !important;
}
</style>
